export type StringCase =
  | "sentence"
  | "title"
  | "camel"
  | "pascal"
  | "kebab"
  | "snake";

export type FormatStringOptions = {
  removeSeparators?: boolean;
  preserveAcronyms?: boolean;
};

export function convertCase(
  input: unknown,
  type: StringCase,
  options: FormatStringOptions = {},
): string {
  if (typeof input !== "string") return "";

  if (!input.trim()) return "";

  const { removeSeparators = true, preserveAcronyms = false } = options;

  let str = input.trim();

  if (removeSeparators) {
    str = removeSeparatorsFn(str);
  }

  str = normalizeSpacing(str);

  switch (type) {
    case "sentence":
      return toSentenceCase(str, preserveAcronyms);

    case "title":
      return toTitleCase(str, preserveAcronyms);

    case "camel":
      return toCamelCase(str);

    case "pascal":
      return toPascalCase(str);

    case "kebab":
      return toKebabCase(str);

    case "snake":
      return toSnakeCase(str);

    default:
      return str;
  }
}

function removeSeparatorsFn(str: string): string {
  return str.replace(/[_-]+/g, " ");
}

function normalizeSpacing(str: string): string {
  return str.replace(/\s+/g, " ");
}

function isAcronym(word: string): boolean {
  return /^[A-Z0-9]{2,}$/.test(word);
}

function toSentenceCase(str: string, preserveAcronyms: boolean): string {
  const parts = str.split(/([.!?]\s*)/);

  return parts
    .map((segment, index) => {
      if (index % 2 !== 0) return segment;

      const words = segment.split(" ").map((word) => {
        if (preserveAcronyms && isAcronym(word)) return word;
        return word.toLowerCase();
      });

      const sentence = words.join(" ").trim();

      return sentence.charAt(0).toUpperCase() + sentence.slice(1);
    })
    .join("");
}

function toTitleCase(str: string, preserveAcronyms: boolean): string {
  return str
    .split(" ")
    .map((word) => {
      if (preserveAcronyms && isAcronym(word)) return word;
      const lower = word.toLowerCase();
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(" ");
}

function toCamelCase(str: string): string {
  const words = str.toLowerCase().split(" ");

  return (
    words[0] +
    words
      .slice(1)
      .map((w) => capitalize(w))
      .join("")
  );
}

function toPascalCase(str: string): string {
  return str.toLowerCase().split(" ").map(capitalize).join("");
}

function toKebabCase(str: string): string {
  return str.toLowerCase().split(" ").join("-");
}

function toSnakeCase(str: string): string {
  return str.toLowerCase().split(" ").join("_");
}

function capitalize(word: string): string {
  if (!word) return "";
  return word.charAt(0).toUpperCase() + word.slice(1);
}
